import dayjs from "dayjs";
import { formatOwnerData } from "../../../utils/ownerData/ownerData.util";
import { BillsTableRow } from "../../models/bills/bills.model";
import { CifCupsState } from "../../models/cupsCifSelector/cupsCifSelector.model";
import { EntitySelectorLocalStorage } from "../../models/entitySelector/entitySelector.model";
import { UserData, HolderData } from "../../models/login/login.model";
import {
  FormattedOwnerData,
  OwnerData,
} from "../../models/ownerData/ownerData.model";
import { GroupedSuppliesTableRow } from "../../models/supplies/supplies.model";
import { DATE_FORMATS } from "../../constants/constants";

const getUserDataFromLocalStorage = (): UserData | null => {
  const userData: string | null = localStorage.getItem(
    process.env.REACT_APP_LOCALSTORAGE_USER_DATA!
  );

  if (userData) {
    const parsedOwnerData: UserData = JSON.parse(userData);
    return parsedOwnerData;
  }

  return null;
};

const setUserDataToLocalStorage = (userData: UserData): void => {
  const localStorageUserData = process.env.REACT_APP_LOCALSTORAGE_USER_DATA!;
  localStorage.setItem(localStorageUserData, JSON.stringify(userData));
};

const setHolderDataToLocalStorage = (holderData: HolderData): void => {
  const localStorageUserData = process.env.REACT_APP_LOCALSTORAGE_HOLDER_DATA!;
  localStorage.setItem(localStorageUserData, JSON.stringify(holderData));
};

const getHolderDataFromLocalStorage = (): HolderData | null => {
  const holderData: string | null = localStorage.getItem(
    process.env.REACT_APP_LOCALSTORAGE_HOLDER_DATA!
  );

  if (holderData) {
    const parsedOwnerData: HolderData = JSON.parse(holderData);
    return parsedOwnerData;
  }

  return null;
};

const getOwnerDataFromLocalStorage = (): FormattedOwnerData | null => {
  const ownerData: string | null = localStorage.getItem(
    process.env.REACT_APP_LOCALSTORAGE_OWNER_DATA!
  );

  if (ownerData) {
    const parsedOwnerData: FormattedOwnerData = JSON.parse(ownerData);
    return parsedOwnerData;
  }

  return null;
};

const setOwnerDataToLocalStorage = (ownerData: OwnerData): void => {
  const localStorageOwnerData = process.env.REACT_APP_LOCALSTORAGE_OWNER_DATA!;
  localStorage.setItem(
    localStorageOwnerData,
    JSON.stringify(formatOwnerData(ownerData))
  );
};

const getCifCupsFromLocalStorage = (): CifCupsState | null => {
  const cifCups: string | null = localStorage.getItem(
    process.env.REACT_APP_LOCALSTORAGE_CIF_CUPS!
  );

  if (cifCups) {
    return JSON.parse(cifCups);
  }

  return null;
};

const setCifCupsToLocalStorage = (cifCups: CifCupsState): void => {
  const localStorageCifCups = process.env.REACT_APP_LOCALSTORAGE_CIF_CUPS!;
  localStorage.setItem(localStorageCifCups, JSON.stringify(cifCups));
};

const setSupplySelectedToLocalStorage = (
  supplySelected: GroupedSuppliesTableRow | null
) => {
  const localStorageSupplySelected =
    process.env.REACT_APP_SESSIONSTORAGE_SUPPLY_SELECTED!;
  sessionStorage.setItem(
    localStorageSupplySelected,
    JSON.stringify(supplySelected)
  );
};

const getSupplySelectedFromLocalStorage =
  (): GroupedSuppliesTableRow | null => {
    const localStorageSupplySelected =
      process.env.REACT_APP_SESSIONSTORAGE_SUPPLY_SELECTED!;
    const supplySelected = sessionStorage.getItem(localStorageSupplySelected);

    if (supplySelected) {
      return JSON.parse(supplySelected);
    }

    return null;
  };

const setBillSelectedToLocalStorage = (billSelected: BillsTableRow | null) => {
  const localStorageBillSelected =
    process.env.REACT_APP_SESSIONSTORAGE_BILL_SELECTED!;
  sessionStorage.setItem(
    localStorageBillSelected,
    JSON.stringify(billSelected)
  );
};

const getBillSelectedFromLocalStorage = (): BillsTableRow | null => {
  const localStorageBillSelected =
    process.env.REACT_APP_SESSIONSTORAGE_BILL_SELECTED!;
  const billSelected = sessionStorage.getItem(localStorageBillSelected);

  if (billSelected) {
    return JSON.parse(billSelected);
  }

  return null;
};

const getEntitySelectionFromLocalStorage =
  (): EntitySelectorLocalStorage | null => {
    const entitySelection: string | null = localStorage.getItem(
      process.env.REACT_APP_LOCALSTORAGE_ENTITY_SELECTION!
    );
    return entitySelection
      ? (JSON.parse(entitySelection) as EntitySelectorLocalStorage)
      : null;
  };

const setEntitySelectionToLocalStorage = (
  entitySelection: EntitySelectorLocalStorage["selection"] | null
): void => {
  const localStorageEntitySelection =
    process.env.REACT_APP_LOCALSTORAGE_ENTITY_SELECTION!;

  if (entitySelection === null) {
    localStorage.removeItem(localStorageEntitySelection);
  } else {
    const selectionToSave: EntitySelectorLocalStorage = {
      selection: entitySelection,
      savedOn: dayjs().format(DATE_FORMATS.LONG_DATE),
    };
    localStorage.setItem(
      localStorageEntitySelection,
      JSON.stringify(selectionToSave)
    );
  }
};

export {
  getOwnerDataFromLocalStorage,
  setUserDataToLocalStorage,
  getUserDataFromLocalStorage,
  setOwnerDataToLocalStorage,
  getCifCupsFromLocalStorage,
  setCifCupsToLocalStorage,
  getSupplySelectedFromLocalStorage,
  setSupplySelectedToLocalStorage,
  getBillSelectedFromLocalStorage,
  setBillSelectedToLocalStorage,
  setHolderDataToLocalStorage,
  getHolderDataFromLocalStorage,
  getEntitySelectionFromLocalStorage,
  setEntitySelectionToLocalStorage,
};
